import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  VStack,
  Button,
  Text,
  Divider,
  Tooltip,
  IconButton,
  Collapse,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import ChatPanel from '../../components/ChatPanel/ChatPanel';
import { useChatOperations } from '../../hooks/useChatOperations';
import Cookies from 'js-cookie';

// const BASE_DB_URL = 'https://dev.database.orcana.ai';
// const BASE_DB_URL = 'http://0.0.0.0:8000';
const BASE_DB_URL = import.meta.env.VITE_BASE_DATABASE_URL;

// ---------------------------------------------------------------------
// Type Definitions
// ---------------------------------------------------------------------
export type Message = {
  message_id: number;
  inquiry_id: number;
  user_id: number;
  created_at: string;
  message_source: 'user' | 'orcana';
  body_original: string;
  body_rephrased: string | null;
  intent: string;
  analytics_run_id: number | null;
  message_type: string | null;
};

export type Inquiry = {
  inquiry_id: number;
  user_id: number;
  org_id: number;
  created_at: string;
  status: 'RESOLVED' | 'UNRESOLVED';
  inquiry_title: string | null;
  last_message_time: string;
};

export type InquiryApiResponse = {
  status: string;
  data: Inquiry[];
};

export type InquiryWithTitle = {
  inquiry: Inquiry;
  title: [string, string];
};

// ---------------------------------------------------------------------
// Helper Function: Categorize Inquiries by Date
// ---------------------------------------------------------------------
const categorizeInquiries = (inquiriesWithTitles: InquiryWithTitle[]) => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const lastWeekStart = new Date(todayStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  return {
    today: inquiriesWithTitles.filter(({ title }) => new Date(title[1]) >= todayStart),
    yesterday: inquiriesWithTitles.filter(({ title }) => {
      const date = new Date(title[1]);
      return date >= yesterdayStart && date < todayStart;
    }),
    lastWeek: inquiriesWithTitles.filter(({ title }) => {
      const date = new Date(title[1]);
      return date >= lastWeekStart && date < yesterdayStart;
    }),
    older: inquiriesWithTitles.filter(({ title }) => new Date(title[1]) < lastWeekStart),
  };
};

// ---------------------------------------------------------------------
// Main Component: OrcanaLive
// ---------------------------------------------------------------------
const OrcanaLive: React.FC = () => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [titles, setTitles] = useState<[string, string][]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [selectedInquiryTitle, setSelectedInquiryTitle] = useState<string>('New Analysis');
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);

  const fetchInquiries = async () => {
    setIsLoadingConversations(true);
    try {
      const token = Cookies.get('accessToken');
      const response = await fetch(`${BASE_DB_URL}/fetch-inquiry-for-user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: InquiryApiResponse = await response.json();

      if (data.status === 'success') {
        setInquiries(data.data);

        const formattedTitles = data.data.map((inquiry) => {
          const formattedDate = new Date(inquiry.last_message_time).toLocaleString(
            'en-US',
            {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }
          );
          const conversationTitle =
            inquiry.inquiry_title && inquiry.inquiry_title !== 'New Chat'
              ? inquiry.inquiry_title
              : 'New Analysis';
          return [conversationTitle, formattedDate] as [string, string];
        });

        setTitles(formattedTitles);
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const {
    messages,
    messageDates,
    input,
    setInput,
    isSending,
    currentInquiryId,
    isConversationSelected,
    handleSendMessage,
    setMessages,
    setMessageDates,
    setCurrentInquiryId,
    setIsConversationSelected,
  } = useChatOperations(fetchInquiries, selectedInquiryTitle);

  // on mount, fetch inquiries but keep "New Analysis" selected
  useEffect(() => {
    fetchInquiries();
    setCurrentInquiryId(null);
    setIsConversationSelected(false);
    setMessages([]);
    setMessageDates([]);
    setInput('');
    setSelectedInquiryTitle('New Analysis');
  }, []);

  const onNewChatCreation = () => {
    setIsConversationSelected(false);
    setCurrentInquiryId(null);
    setMessages([]);
    setMessageDates([]);
    setInput('');
    setSelectedInquiryTitle('New Analysis');
  };

  const handleInquiryClick = async (inquiryId: number) => {
    setCurrentInquiryId(inquiryId);
    try {
      const token = Cookies.get('accessToken');
      const response = await fetch(
        `${BASE_DB_URL}/fetch-messages-for-inquiry?inquiry_id=${inquiryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.status === 'success') {
        const sortedMsgs = data.data.sort(
          (a: Message, b: Message) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        setMessages(sortedMsgs);
        setMessageDates(
          sortedMsgs.map((m: Message) =>
            new Date(m.created_at).toLocaleString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
          )
        );
        setIsConversationSelected(true);

        const sel = inquiries.find((i) => i.inquiry_id === inquiryId);
        setSelectedInquiryTitle(sel?.inquiry_title || 'New Analysis');
        // fetchInquiries();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // build, sort, limit and categorize once per data change
  const inquiriesWithTitles = useMemo<InquiryWithTitle[]>(() => {
    return inquiries.map((inq, idx) => ({
      inquiry: inq,
      title: titles[idx] || ['Loading...', new Date().toISOString()],
    }));
  }, [inquiries, titles]);

  const sorted = useMemo<InquiryWithTitle[]>(() => {
    return [...inquiriesWithTitles].sort(
      (a, b) => new Date(b.title[1]).getTime() - new Date(a.title[1]).getTime()
    );
  }, [inquiriesWithTitles]);

  const topTwentyFive = useMemo<InquiryWithTitle[]>(() => sorted.slice(0, 25), [sorted]);

  const categorized = useMemo(() => categorizeInquiries(topTwentyFive), [topTwentyFive]);

  return (
    <Box h="100%" bg="gray.100" maxW="100vw" overflow="hidden">
      <Flex h="100%">
        {/* Sidebar */}
        <Box
          w={sidebarCollapsed ? '4rem' : { base: '100%', md: '20%' }}
          minW={sidebarCollapsed ? '4rem' : '200px'}
          bg="white"
          borderRight="1px solid"
          borderColor="gray.200"
          overflow="hidden"
          h="100%"
          overflowY="auto"
          transition="width 0.2s ease"
          boxShadow="md"
        >
          {/* Collapse/Expand Header */}
          <Flex
            align="center"
            justify={sidebarCollapsed ? 'center' : 'space-between'}
            px={sidebarCollapsed ? 0 : 4}
            py={2}
          >
            {!sidebarCollapsed && (
              <Text fontSize="xl" fontWeight="bold" color="#4A4A4A">
                Conversations
              </Text>
            )}
            <IconButton
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              icon={sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          </Flex>

          <Divider />

          {/* Collapsible Content */}
          <Collapse in={!sidebarCollapsed} animateOpacity>
            <VStack spacing={4} align="stretch" p={4}>
              <Button variant="solid" bg="secondary.500" w="100%" onClick={onNewChatCreation}>
                New Analysis
              </Button>

              {categorized.today.length > 0 && (
                <>
                  <Text fontSize="sm" color="gray.500" fontWeight="semibold">
                    Today
                  </Text>
                  {categorized.today.map(({ inquiry, title }) => (
                    <InquiryButton
                      key={inquiry.inquiry_id}
                      inquiry={inquiry}
                      title={title}
                      onClick={handleInquiryClick}
                      currentInquiryId={currentInquiryId}
                    />
                  ))}
                </>
              )}
              {categorized.yesterday.length > 0 && (
                <>
                  <Text fontSize="sm" color="gray.500" fontWeight="semibold">
                    Yesterday
                  </Text>
                  {categorized.yesterday.map(({ inquiry, title }) => (
                    <InquiryButton
                      key={inquiry.inquiry_id}
                      inquiry={inquiry}
                      title={title}
                      onClick={handleInquiryClick}
                      currentInquiryId={currentInquiryId}
                    />
                  ))}
                </>
              )}
              {categorized.lastWeek.length > 0 && (
                <>
                  <Text fontSize="sm" color="gray.500" fontWeight="semibold">
                    Last Week
                  </Text>
                  {categorized.lastWeek.map(({ inquiry, title }) => (
                    <InquiryButton
                      key={inquiry.inquiry_id}
                      inquiry={inquiry}
                      title={title}
                      onClick={handleInquiryClick}
                      currentInquiryId={currentInquiryId}
                    />
                  ))}
                </>
              )}
              {categorized.older.length > 0 && (
                <>
                  <Text fontSize="sm" color="gray.500" fontWeight="semibold">
                    Older
                  </Text>
                  {categorized.older.map(({ inquiry, title }) => (
                    <InquiryButton
                      key={inquiry.inquiry_id}
                      inquiry={inquiry}
                      title={title}
                      onClick={handleInquiryClick}
                      currentInquiryId={currentInquiryId}
                    />
                  ))}
                </>
              )}
            </VStack>
          </Collapse>
        </Box>

        {/* Main Chat Area */}
        <Flex direction="column" flex="1">
          <ChatPanel
            messages={messages}
            messageDates={messageDates}
            input={input}
            setInput={setInput}
            handleSendMessage={handleSendMessage}
            isLoading={isSending}
            isConversationSelected={isConversationSelected}
            isLoadingConversations={isLoadingConversations}
          />
        </Flex>
      </Flex>
    </Box>
  );
};

// ---------------------------------------------------------------------
// InquiryButton Component (memoized)
// ---------------------------------------------------------------------
type InquiryButtonProps = {
  inquiry: Inquiry;
  title: [string, string];
  onClick: (id: number) => void;
  currentInquiryId: number | null;
};

const InquiryButton = React.memo(({ inquiry, title, onClick, currentInquiryId }: InquiryButtonProps) => (
  <Tooltip
    label={
      <Text fontSize="xs" color="gray.500">
        {new Date(title[1]).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </Text>
    }
    placement="right"
    hasArrow
    bg="white"
    color="gray.800"
    boxShadow="md"
  >
    <Button
      variant="ghost"
      justifyContent="flex-start"
      w="100%"
      py={3}
      _hover={{ bg: 'gray.100' }}
      fontSize="md"
      color="#333333"
      fontWeight={currentInquiryId === inquiry.inquiry_id ? 'bold' : 'medium'}
      bg={currentInquiryId === inquiry.inquiry_id ? 'secondary.500' : 'transparent'}
      whiteSpace="normal"
      wordBreak="break-word"
      textAlign="left"
      onClick={() => onClick(inquiry.inquiry_id)}
    >
      {title[0]}
    </Button>
  </Tooltip>
));

export default OrcanaLive;
